import {
  CLAIM_STATUS,
  REQUEST_TYPE,
  RETURN_DELIVERED_TRACKING_STATUS,
  RETURN_PRE_TRANSIT_TRACKING_STATUS,
  RETURN_STAGE,
} from "../constants";
import { formatMoney } from "../formatters";
import type { Claim, ClaimItem, ClaimTranslator, ReturnLabel } from "../types";

export type ReturnStage = (typeof RETURN_STAGE)[keyof typeof RETURN_STAGE];

const RETURN_REQUEST_TYPES: string[] = [
  REQUEST_TYPE.RETURN,
  REQUEST_TYPE.EXCHANGE,
];

export const isReturnRequest = (claim: Claim) =>
  RETURN_REQUEST_TYPES.includes(claim.request_type ?? "");

export const CLAIM_REQUEST_TERM = "claim";
export const MIXED_REQUEST_TERM = "request";

const mixesExchangeWithOtherResolutions = (claim: Claim) => {
  const items = claim.claim_items ?? [];
  return (
    items.some((item) => item.exchange_variant_title) &&
    items.some((item) => !item.exchange_variant_title)
  );
};

export const requestTerm = (claim: Claim): string => {
  if (!isReturnRequest(claim)) return CLAIM_REQUEST_TERM;
  return mixesExchangeWithOtherResolutions(claim)
    ? MIXED_REQUEST_TERM
    : (claim.request_type as string);
};

const REFUND_RESOLUTION_PATTERN = /refund/i;

export const itemResolutionText = (
  item: ClaimItem,
  currency: string | undefined,
  t: ClaimTranslator,
): string | null => {
  if (item.exchange_variant_title) {
    return t("status.itemExchange", { title: item.exchange_variant_title });
  }
  const resolution = item.preferred_resolution?.replace(/_/g, " ");
  if (!resolution) return null;
  if (!REFUND_RESOLUTION_PATTERN.test(resolution) || item.price == null) {
    return resolution;
  }
  const amount = formatMoney(item.price * (item.quantity || 1), currency);
  return `${resolution} ${amount}`;
};

export const hasItemResolutions = (claim: Claim) =>
  (claim.claim_items ?? []).some(
    (item) => item.preferred_resolution || item.exchange_variant_title,
  );

export const getActiveReturnLabel = (claim: Claim): ReturnLabel | null => {
  const labels = claim.return_labels ?? [];
  return labels[labels.length - 1] ?? null;
};

const isReturnResolved = (claim: Claim) =>
  claim.claim_status === CLAIM_STATUS.APPROVED ||
  claim.claim_status === CLAIM_STATUS.CLOSED;

const isLabelDelivered = (label: ReturnLabel) =>
  Boolean(label.delivered_at) ||
  label.tracking_status?.toLowerCase() === RETURN_DELIVERED_TRACKING_STATUS;

const hasCarrierScan = (label: ReturnLabel) => {
  const trackingStatus = label.tracking_status?.toLowerCase();
  return (
    Boolean(trackingStatus) &&
    trackingStatus !== RETURN_PRE_TRANSIT_TRACKING_STATUS
  );
};

export const getReturnStage = (claim: Claim): ReturnStage => {
  if (claim.claim_status === CLAIM_STATUS.DENIED) {
    return RETURN_STAGE.DENIED;
  }
  if (isReturnResolved(claim)) {
    return RETURN_STAGE.RESOLVED;
  }

  const label = getActiveReturnLabel(claim);
  if (!label) {
    return RETURN_STAGE.REQUESTED;
  }
  if (isLabelDelivered(label)) {
    return RETURN_STAGE.RECEIVED;
  }
  return hasCarrierScan(label)
    ? RETURN_STAGE.IN_TRANSIT
    : RETURN_STAGE.LABEL_READY;
};

const LABEL_VISIBLE_STAGES: ReturnStage[] = [
  RETURN_STAGE.LABEL_READY,
  RETURN_STAGE.IN_TRANSIT,
];

export const getVisibleReturnLabel = (claim: Claim): ReturnLabel | null =>
  isReturnRequest(claim) && LABEL_VISIBLE_STAGES.includes(getReturnStage(claim))
    ? getActiveReturnLabel(claim)
    : null;
